/* eslint-disable */
import {
  GraduationCap, Shield, Code, Network, CheckCircle,
  Cloud, BarChart2, GitBranch, Smartphone, Database,
  Terminal, Wifi, Lock, Clock, Award, BookOpen, Phone, Bot,
} from "lucide-react";

export const ARROW_COLOR_MAP = {
  "bg-blue-900":   "#1e3a8a",
  "bg-blue-700":   "#1d4ed8",
  "bg-indigo-700": "#4338ca",
  "bg-violet-700": "#6d28d9",
  "bg-purple-800": "#6b21a8",
  "bg-fuchsia-700":"#a21caf",
  "bg-red-700":    "#b91c1c",
  "bg-rose-800":   "#9f1239",
  "bg-pink-700":   "#be185d",
  "bg-sky-600":    "#0284c7",
  "bg-teal-700":   "#0f766e",
  "bg-cyan-700":   "#0e7490",
  "bg-emerald-700":"#047857",
  "bg-green-800":  "#166534",
  "bg-lime-800":   "#3f6212",
  "bg-orange-600": "#ea580c",
  "bg-amber-700":  "#b45309",
  "bg-yellow-700": "#a16207",
  "bg-gray-800":   "#1f2937",
  "bg-stone-700":  "#44403c",
};

const COURSE_CARDS_I18N = {
  en: [
  {
    title: "IT & System Engineering",
    icon: GraduationCap,
    desc: "OS, hardware, and real system administration from the ground up.",
    bg: "bg-blue-900",
    duration: "4 months",
    level: "Beginner → Mid",
    cert: "CompTIA A+",
    detail: {
      overview:
        "This course takes you from zero to a confident IT professional. You will learn how computers work at the hardware level, master operating systems like Windows and Linux, and practice real system administration tasks used in enterprise environments every day.",
      topics: [
        "Computer hardware components and assembly",
        "Windows & Linux OS installation and configuration",
        "File systems, permissions, and user management",
        "Active Directory and Group Policy basics",
        "Troubleshooting methodology and helpdesk skills",
        "Virtualization with VMware and VirtualBox",
      ],
      outcome:
        "Graduate as a junior IT administrator ready for helpdesk, sysadmin, or IT support roles at real companies.",
    },
  },
  {
    title: "Networking & Security",
    icon: Network,
    desc: "Network design, routing, switching, and administration basics.",
    bg: "bg-indigo-700",
    duration: "5 months",
    level: "Beginner → Mid",
    cert: "Cisco CCNA",
    detail: {
      overview:
        "Dive deep into the world of computer networks. From understanding how data travels across the internet to configuring routers and switches in a lab environment, this course gives you the hands-on skills that employers in networking demand.",
      topics: [
        "OSI and TCP/IP model fundamentals",
        "IP addressing, subnetting, and VLSM",
        "Cisco router and switch configuration (CLI)",
        "VLANs, STP, EtherChannel, and trunking",
        "OSPF, EIGRP, and BGP routing protocols",
        "Network monitoring and troubleshooting tools",
      ],
      outcome:
        "Be prepared for Cisco CCNA certification and roles such as Network Administrator, NOC Engineer, or Network Analyst.",
    },
  },
  {
    title: "Cybersecurity",
    icon: Shield,
    desc: "Defensive security, ethical hacking, and risk mindset.",
    bg: "bg-red-700",
    duration: "5 months",
    level: "Mid level",
    cert: "CompTIA Security+",
    detail: {
      overview:
        "Cybersecurity is the most in-demand skill in IT today. This course teaches you how attackers think and how defenders respond. You will work in real lab environments simulating attacks and building defenses, developing the mindset of a true security professional.",
      topics: [
        "Threats, vulnerabilities, and attack vectors",
        "Firewalls, IDS/IPS, and SIEM systems",
        "Cryptography and PKI fundamentals",
        "Security policies, risk management, and compliance",
        "Incident response and digital forensics basics",
        "Hands-on labs with Kali Linux and Wireshark",
      ],
      outcome:
        "Land roles as a Security Analyst, SOC Analyst, or IT Security Specialist with a strong foundation for advanced certifications.",
    },
  },
  {
    title: "Software Development",
    icon: Code,
    desc: "Backend, frontend, and full-stack web development tracks.",
    bg: "bg-violet-700",
    duration: "6 months",
    level: "Beginner → Advanced",
    cert: "CodeKloud Certificate",
    detail: {
      overview:
        "Whether you want to build websites, APIs, or full applications, this comprehensive development track covers everything. You will write real code from day one, build portfolio projects, and learn modern development workflows used in top tech companies.",
      topics: [
        "HTML, CSS, and JavaScript fundamentals",
        "React.js for modern frontend development",
        "Python or Node.js for backend development",
        "REST APIs and database integration",
        "Git, GitHub, and collaborative coding",
        "Deploying apps to the cloud (Heroku, Vercel, AWS)",
      ],
      outcome:
        "Build a professional portfolio with 3–5 real projects and qualify for junior developer roles in web or backend development.",
    },
  },
  {
    title: "Cloud Computing",
    icon: Cloud,
    desc: "AWS, Azure, and GCP — cloud infrastructure and deployment.",
    bg: "bg-sky-600",
    duration: "4 months",
    level: "Mid level",
    cert: "AWS Solutions Architect",
    detail: {
      overview:
        "Cloud is no longer optional — it is the backbone of modern IT. This course teaches you how to architect, deploy, and manage infrastructure on the world's top cloud platforms. Hands-on labs run on real AWS, Azure, and GCP accounts.",
      topics: [
        "Cloud concepts: IaaS, PaaS, SaaS explained",
        "AWS core services: EC2, S3, RDS, VPC, IAM",
        "Azure fundamentals and Azure AD",
        "Load balancing, auto-scaling, and high availability",
        "Cloud security best practices and cost optimization",
        "Infrastructure as Code with Terraform basics",
      ],
      outcome:
        "Earn AWS, Azure, or GCP certifications and work as a Cloud Engineer, Cloud Architect, or DevOps Engineer.",
    },
  },
  {
    title: "Data Analytics",
    icon: BarChart2,
    desc: "SQL, Python, Power BI — turn raw data into real decisions.",
    bg: "bg-emerald-700",
    duration: "4 months",
    level: "Beginner → Mid",
    cert: "Microsoft PL-300",
    detail: {
      overview:
        "Data is the new oil, and analysts are the engineers who refine it. This course teaches you to collect, clean, analyze, and visualize data to drive business decisions. No prior experience needed — just curiosity and the drive to find meaning in numbers.",
      topics: [
        "SQL from basics to advanced queries and joins",
        "Python with pandas, NumPy, and Matplotlib",
        "Data cleaning and transformation techniques",
        "Power BI dashboard design and DAX formulas",
        "Statistical thinking and KPI analysis",
        "Presenting data insights to non-technical stakeholders",
      ],
      outcome:
        "Step into Data Analyst, Business Intelligence, or Reporting Analyst roles at companies that rely on data-driven decisions.",
    },
  },
  {
    title: "DevOps & CI/CD",
    icon: GitBranch,
    desc: "Docker, Kubernetes, Jenkins — automate and ship faster.",
    bg: "bg-orange-600",
    duration: "5 months",
    level: "Mid → Advanced",
    cert: "CKA / Docker DCA",
    detail: {
      overview:
        "DevOps bridges the gap between development and operations. You will learn to automate everything — from code integration to deployment pipelines — using the tools that power Netflix, Amazon, and Google. Labs run on real infrastructure.",
      topics: [
        "Linux for DevOps: shell scripting and automation",
        "Docker: containers, images, and Docker Compose",
        "Kubernetes: pods, deployments, services, and Helm",
        "Jenkins and GitHub Actions CI/CD pipelines",
        "Ansible for configuration management",
        "Monitoring with Prometheus and Grafana",
      ],
      outcome:
        "Qualify for high-demand DevOps Engineer, Site Reliability Engineer, or Platform Engineer roles with strong salaries.",
    },
  },
  {
    title: "Mobile Development",
    icon: Smartphone,
    desc: "Android & iOS apps with React Native and Flutter.",
    bg: "bg-pink-700",
    duration: "5 months",
    level: "Beginner → Mid",
    cert: "CodeKloud Certificate",
    detail: {
      overview:
        "Build real mobile applications that run on both Android and iOS from a single codebase. You will learn cross-platform development frameworks, UI design principles for mobile, and how to publish your apps to the App Store and Google Play.",
      topics: [
        "JavaScript and Dart language fundamentals",
        "React Native: components, navigation, and state",
        "Flutter: widgets, themes, and animations",
        "Connecting apps to REST APIs and Firebase",
        "Local storage, push notifications, and permissions",
        "App Store and Google Play publishing process",
      ],
      outcome:
        "Launch your own app or work as a Mobile Developer building products used by thousands of real users.",
    },
  },
  {
    title: "Database Administration",
    icon: Database,
    desc: "MySQL, PostgreSQL, MongoDB — design and manage databases.",
    bg: "bg-teal-700",
    duration: "3 months",
    level: "Beginner → Mid",
    cert: "Oracle / MySQL Cert",
    detail: {
      overview:
        "Every application needs a reliable database behind it. This course teaches you to design, optimize, and administer both relational and NoSQL databases. You will handle real-world scenarios like backups, replication, and performance tuning.",
      topics: [
        "Relational database design and normalization",
        "MySQL and PostgreSQL administration",
        "Advanced SQL: stored procedures, views, triggers",
        "MongoDB and NoSQL data modeling",
        "Database backup, recovery, and replication",
        "Query optimization and performance tuning",
      ],
      outcome:
        "Work as a Database Administrator, Backend Developer, or Data Engineer managing critical data infrastructure.",
    },
  },
  {
    title: "Linux & Server Admin",
    icon: Terminal,
    desc: "Linux fundamentals, shell scripting, and server management.",
    bg: "bg-gray-800",
    duration: "3 months",
    level: "Beginner → Mid",
    cert: "CompTIA Linux+ / RHCSA",
    detail: {
      overview:
        "Linux powers over 90% of the world's servers. Mastering it is non-negotiable for any serious IT career. This course takes you from basic commands to writing complex shell scripts and managing production Linux servers with confidence.",
      topics: [
        "Linux file system hierarchy and navigation",
        "User and group management, permissions and ACLs",
        "Process management and system monitoring",
        "Bash shell scripting for automation",
        "Package management: apt, yum, and dnf",
        "Web, DNS, and SSH server configuration",
      ],
      outcome:
        "Become the Linux expert your team relies on, and open doors to sysadmin, DevOps, and cloud engineering careers.",
    },
  },
  {
    title: "Wireless & IoT",
    icon: Wifi,
    desc: "Wi-Fi technologies, IoT systems, and smart device management.",
    bg: "bg-cyan-700",
    duration: "3 months",
    level: "Mid level",
    cert: "CWNA / IoT Certificate",
    detail: {
      overview:
        "The world is becoming wireless and connected. This course covers enterprise Wi-Fi design, wireless security, and the rapidly growing Internet of Things ecosystem. You will work with real devices, sensors, and wireless controllers.",
      topics: [
        "802.11 Wi-Fi standards and RF fundamentals",
        "Wireless network design and site surveys",
        "WPA3, RADIUS, and wireless security best practices",
        "IoT architecture: sensors, gateways, and platforms",
        "MQTT protocol and data communication for IoT",
        "Smart home and industrial IoT use cases",
      ],
      outcome:
        "Work as a Wireless Network Engineer or IoT Solutions Specialist in the rapidly expanding connected-devices industry.",
    },
  },
  {
    title: "Ethical Hacking & Pentesting",
    icon: Lock,
    desc: "Penetration testing, vulnerability scanning, and attack simulation.",
    bg: "bg-rose-800",
    duration: "5 months",
    level: "Mid → Advanced",
    cert: "CEH / eJPT / OSCP",
    detail: {
      overview:
        "Ethical hackers are paid to break things — legally. This advanced course teaches you to think like an attacker, systematically find vulnerabilities, and report them to protect organizations. Labs simulate real-world environments with zero boundaries.",
      topics: [
        "Penetration testing methodology (recon, scanning, exploitation)",
        "Kali Linux tools: Nmap, Metasploit, Burp Suite",
        "Web application attacks: SQLi, XSS, CSRF, IDOR",
        "Password cracking and privilege escalation techniques",
        "Active Directory attacks and lateral movement",
        "Writing professional penetration test reports",
      ],
      outcome:
        "Become a certified Penetration Tester or Bug Bounty Hunter — one of the most exciting and well-paying jobs in tech.",
    },
  },
  {
    title: "Machine Learning & AI",
    icon: Bot,
    desc: "Python, TensorFlow, and real ML model building.",
    bg: "bg-purple-800",
    duration: "6 months",
    level: "Mid → Advanced",
    cert: "TensorFlow Developer Cert",
    detail: {
      overview:
        "Artificial Intelligence is reshaping every industry. This course teaches you to build, train, and deploy real machine learning models. You will work with Python, TensorFlow, and scikit-learn on datasets that simulate real business problems.",
      topics: [
        "Python for data science: NumPy, pandas, Matplotlib",
        "Supervised and unsupervised learning algorithms",
        "Neural networks and deep learning fundamentals",
        "TensorFlow and Keras model training",
        "Natural language processing and computer vision basics",
        "Model deployment with Flask and cloud APIs",
      ],
      outcome:
        "Enter the field as a Machine Learning Engineer, AI Developer, or Data Scientist — among the highest-paid roles in tech.",
    },
  },
  {
    title: "IT Project Management",
    icon: Clock,
    desc: "Agile, Scrum, PMP — manage tech projects end-to-end.",
    bg: "bg-amber-700",
    duration: "3 months",
    level: "Beginner → Mid",
    cert: "PMI / Scrum PSM I",
    detail: {
      overview:
        "Every IT team needs leaders who can plan, coordinate, and deliver projects on time. This course teaches you Agile and traditional project management methodologies used by top companies worldwide, with real case studies and hands-on sprint simulations.",
      topics: [
        "Project lifecycle: initiation, planning, execution, closure",
        "Agile manifesto and Scrum framework deep dive",
        "Sprint planning, backlog grooming, and retrospectives",
        "Risk management and stakeholder communication",
        "Jira and Trello for project tracking",
        "PMP and PSM I exam preparation",
      ],
      outcome:
        "Step into IT Project Manager, Scrum Master, or Product Owner roles and lead cross-functional teams with confidence.",
    },
  },
  {
    title: "UI/UX Design",
    icon: Award,
    desc: "Figma, user research, and product design principles.",
    bg: "bg-fuchsia-700",
    duration: "4 months",
    level: "Beginner → Mid",
    cert: "Google UX Design Cert",
    detail: {
      overview:
        "Great products need great design. This course teaches you to research users, design interfaces, and create interactive prototypes that developers can build from. You will use Figma — the industry-standard tool — to build a real design portfolio.",
      topics: [
        "User research: interviews, surveys, and personas",
        "Information architecture and user flows",
        "Wireframing and lo-fi prototyping",
        "High-fidelity UI design in Figma",
        "Usability testing and design iteration",
        "Handoff to developers with design systems",
      ],
      outcome:
        "Build a Figma portfolio of 3+ projects and land roles as a UI/UX Designer, Product Designer, or UX Researcher.",
    },
  },
  {
    title: "Blockchain & Web3",
    icon: GitBranch,
    desc: "Solidity, smart contracts, and decentralized apps.",
    bg: "bg-lime-800",
    duration: "4 months",
    level: "Mid → Advanced",
    cert: "Certified Blockchain Developer",
    detail: {
      overview:
        "Blockchain is moving beyond crypto into supply chain, healthcare, finance, and beyond. This course teaches you to write smart contracts in Solidity, build decentralized applications, and understand the architecture of modern blockchain networks.",
      topics: [
        "Blockchain fundamentals: consensus, nodes, and chains",
        "Ethereum ecosystem and EVM explained",
        "Solidity programming: contracts, functions, events",
        "Hardhat and Truffle development environments",
        "Web3.js and ethers.js for DApp frontends",
        "DeFi protocols and NFT smart contract development",
      ],
      outcome:
        "Work as a Blockchain Developer or Smart Contract Engineer in the fast-growing Web3 industry.",
    },
  },
  {
    title: "Digital Marketing & SEO",
    icon: BarChart2,
    desc: "Google Ads, SEO, social media, and analytics.",
    bg: "bg-green-800",
    duration: "3 months",
    level: "Beginner → Mid",
    cert: "Google Digital Marketing Cert",
    detail: {
      overview:
        "Every business needs digital presence. This practical course covers the full digital marketing toolkit — from search engine optimization to paid advertising and social media strategy — using real campaigns and analytics tools.",
      topics: [
        "SEO fundamentals: on-page, off-page, and technical",
        "Google Ads: search, display, and shopping campaigns",
        "Social media marketing: Instagram, LinkedIn, Facebook",
        "Email marketing and marketing automation",
        "Google Analytics 4 and conversion tracking",
        "Content strategy and copywriting for the web",
      ],
      outcome:
        "Launch campaigns independently or join agencies and in-house teams as a Digital Marketing Specialist or SEO Analyst.",
    },
  },
  {
    title: "Quality Assurance & Testing",
    icon: CheckCircle,
    desc: "Manual and automated testing with Selenium and Cypress.",
    bg: "bg-yellow-700",
    duration: "4 months",
    level: "Beginner → Mid",
    cert: "ISTQB Foundation",
    detail: {
      overview:
        "Every great product needs a QA engineer to catch bugs before users do. This course covers both manual and automated testing strategies, teaching you to write test cases, build automation frameworks, and integrate testing into CI/CD pipelines.",
      topics: [
        "Software testing fundamentals and SDLC integration",
        "Test case design: equivalence partitioning, boundary values",
        "Manual testing: web, mobile, and API testing",
        "Selenium WebDriver with Java or Python",
        "Cypress for modern JavaScript application testing",
        "API testing with Postman and REST Assured",
      ],
      outcome:
        "Join any software team as a QA Engineer or Test Automation Engineer — roles in high demand at every software company.",
    },
  },
  {
    title: "ERP & Business Systems",
    icon: BookOpen,
    desc: "SAP, Odoo, and enterprise resource planning fundamentals.",
    bg: "bg-stone-700",
    duration: "3 months",
    level: "Beginner → Mid",
    cert: "SAP Certified Associate",
    detail: {
      overview:
        "Enterprise systems run the backend of every large business. This course introduces you to ERP concepts and gives you hands-on experience with SAP and Odoo — the platforms used by thousands of companies to manage finance, HR, and operations.",
      topics: [
        "ERP concepts: modules, integration, and business processes",
        "SAP S/4HANA navigation and core modules (FI, MM, SD)",
        "Odoo community edition setup and configuration",
        "Business process mapping and requirements analysis",
        "Data migration and system integration basics",
        "ERP implementation project lifecycle",
      ],
      outcome:
        "Work as an ERP Consultant, SAP Analyst, or Business Systems Specialist at enterprises modernizing their operations.",
    },
  },
  {
    title: "IT Support & Helpdesk",
    icon: Phone,
    desc: "Helpdesk skills, ticketing systems, and customer support.",
    bg: "bg-blue-700",
    duration: "2 months",
    level: "Beginner",
    cert: "CompTIA A+ / HDI",
    detail: {
      overview:
        "IT support is the entry point for thousands of successful IT careers. This course prepares you to handle real user issues confidently, use professional ticketing systems, and provide excellent technical customer service in any environment.",
      topics: [
        "Hardware and software troubleshooting methodology",
        "Windows 10/11 and macOS support essentials",
        "Active Directory user management and password resets",
        "Ticketing systems: ServiceNow and Jira Service Desk",
        "Remote support tools: TeamViewer, AnyDesk, RDP",
        "Communication skills and escalation procedures",
      ],
      outcome:
        "Land your first IT role as a Helpdesk Technician or IT Support Specialist and build from there into any IT specialization.",
    },
  },
  ],
  az: [
  {
    title: "İT və Sistem Mühəndisliyi",
    icon: GraduationCap,
    desc: "ƏS, hardware və real sistem administrasiyası — sıfırdan başlayaraq.",
    bg: "bg-blue-900",
    duration: "4 ay",
    level: "Başlanğıc → Orta",
    cert: "CompTIA A+",
    detail: {
      overview:
        "Bu kurs sizi sıfırdan özünəinamlı IT mütəxəssisinə çevirir. Kompüterlərin hardware səviyyəsində necə işlədiyini öyrənəcək, Windows və Linux əməliyyat sistemlərini mənimsəyəcək və hər gün müəssisə mühitlərində istifadə olunan real sistem administrasiyası tapşırıqlarını yerinə yetirəcəksiniz.",
      topics: [
        "Kompüter hardware komponentləri və yığılması",
        "Windows və Linux ƏS quraşdırılması və konfiqurasiyası",
        "Fayl sistemləri, icazələr və istifadəçi idarəetməsi",
        "Active Directory və Qrup Siyasəti əsasları",
        "Troubleshooting metodologiyası və helpdesk bacarıqları",
        "VMware və VirtualBox ilə virtualizasiya",
      ],
      outcome:
        "Real şirkətlərdə helpdesk, sysadmin və ya IT dəstək rollarına hazır junior IT administratoru kimi məzun olun.",
    },
  },
  {
    title: "Şəbəkə və Təhlükəsizlik",
    icon: Network,
    desc: "Şəbəkə dizaynı, marşrutlaşdırma, kommutasiya və administrasiya əsasları.",
    bg: "bg-indigo-700",
    duration: "5 ay",
    level: "Başlanğıc → Orta",
    cert: "Cisco CCNA",
    detail: {
      overview:
        "Kompüter şəbəkələri dünyasına dərindən dalın. Datanın internetdə necə ötürüldüyündən tutmuş lab mühitində router və switch konfiqurasiyasına qədər bu kurs işəgötürənlərin tələb etdiyi praktiki bacarıqları verir.",
      topics: [
        "OSI və TCP/IP model əsasları",
        "IP ünvanlama, subnetting və VLSM",
        "Cisco router və switch konfiqurasiyası (CLI)",
        "VLAN, STP, EtherChannel və trunking",
        "OSPF, EIGRP və BGP marşrutlaşdırma protokolları",
        "Şəbəkə monitorinqi və troubleshooting alətləri",
      ],
      outcome:
        "Cisco CCNA sertifikatına və Şəbəkə Administratoru, NOC Mühəndisi və ya Şəbəkə Analitiki kimi rollara hazır olun.",
    },
  },
  {
    title: "Kibertəhlükəsizlik",
    icon: Shield,
    desc: "Müdafiəli təhlükəsizlik, etik hakerlik və risk düşüncəsi.",
    bg: "bg-red-700",
    duration: "5 ay",
    level: "Orta səviyyə",
    cert: "CompTIA Security+",
    detail: {
      overview:
        "Kibertəhlükəsizlik bu gün IT sahəsindəki ən tələbkar bacarıqdır. Bu kurs sizə hücumçuların necə düşündüyünü və müdafiəçilərin necə cavab verdiyini öyrədir. Real lab mühitlərində hücumlar simulyasiya edəcək və müdafiə sistemləri quracaqsınız.",
      topics: [
        "Təhdidlər, zəifliklər və hücum vektorları",
        "Firewall, IDS/IPS və SIEM sistemləri",
        "Kriptoqrafiya və PKI əsasları",
        "Təhlükəsizlik siyasətləri, risk idarəetməsi və uyğunluq",
        "İnsident cavabı və rəqəmsal məhkəmə əsasları",
        "Kali Linux və Wireshark ilə praktiki lablar",
      ],
      outcome:
        "Güclü əsasla Təhlükəsizlik Analitiki, SOC Analitiki və ya IT Təhlükəsizlik Mütəxəssisi kimi rollara nail olun.",
    },
  },
  {
    title: "Proqram Təminatı İnkişafı",
    icon: Code,
    desc: "Backend, frontend və full-stack veb inkişaf istiqamətləri.",
    bg: "bg-violet-700",
    duration: "6 ay",
    level: "Başlanğıc → İrəli",
    cert: "CodeKloud Sertifikatı",
    detail: {
      overview:
        "Vebsayt, API və ya tam tətbiq qurmaq istəyirsinizsə, bu kurs hər şeyi əhatə edir. Birinci gündən real kod yazacaq, portfel layihələri quracaq və ən yaxşı texnologiya şirkətlərindəki müasir inkişaf iş axışlarını öyrənəcəksiniz.",
      topics: [
        "HTML, CSS və JavaScript əsasları",
        "Müasir frontend üçün React.js",
        "Backend üçün Python və ya Node.js",
        "REST API-lər və verilənlər bazası inteqrasiyası",
        "Git, GitHub və kollektiv kodlaşdırma",
        "Tətbiqlərin buluda yerləşdirilməsi (Heroku, Vercel, AWS)",
      ],
      outcome:
        "3–5 real layihədən ibarət peşəkar portfel qurun və veb və ya backend inkişafında junior developer rollarına uyğun olun.",
    },
  },
  {
    title: "Bulud Hesablaması",
    icon: Cloud,
    desc: "AWS, Azure və GCP — bulud infrastrukturu və yerləşdirmə.",
    bg: "bg-sky-600",
    duration: "4 ay",
    level: "Orta səviyyə",
    cert: "AWS Solutions Architect",
    detail: {
      overview:
        "Bulud artıq seçim deyil — müasir IT-nin əsasıdır. Bu kurs dünyının ən yaxşı bulud platformalarında infrastruktur qurmağı, yerləşdirməyi və idarə etməyi öyrədir. Lablar real AWS, Azure və GCP hesablarında keçirilir.",
      topics: [
        "Bulud konseptləri: IaaS, PaaS, SaaS izahı",
        "AWS əsas xidmətlər: EC2, S3, RDS, VPC, IAM",
        "Azure əsasları və Azure AD",
        "Yük balansı, avtomatik miqyaslama və yüksək əlçatımlılıq",
        "Bulud təhlükəsizliyi ən yaxşı təcrübələri və xərc optimallaşdırması",
        "Terraform ilə Kod kimi İnfrastruktur əsasları",
      ],
      outcome:
        "AWS, Azure və ya GCP sertifikatları qazanın və Bulud Mühəndisi, Bulud Memarı və ya DevOps Mühəndisi kimi çalışın.",
    },
  },
  {
    title: "Məlumat Analitikası",
    icon: BarChart2,
    desc: "SQL, Python, Power BI — xam məlumatı real qərarlara çevirin.",
    bg: "bg-emerald-700",
    duration: "4 ay",
    level: "Başlanğıc → Orta",
    cert: "Microsoft PL-300",
    detail: {
      overview:
        "Məlumat yeni neftdir, analistlər isə onu emal edən mühəndislərdir. Bu kurs iş qərarlarını dəstəkləmək üçün məlumatları toplamağı, təmizləməyi, analiz etməyi və vizuallaşdırmağı öyrədir. Əvvəlki təcrübə lazım deyil.",
      topics: [
        "SQL əsaslardan irəli sorğulara və birləşmələrə qədər",
        "pandas, NumPy və Matplotlib ilə Python",
        "Məlumat təmizləmə və çevirmə texnikaları",
        "Power BI panel dizaynı və DAX formulları",
        "Statistik düşüncə və KPI analizi",
        "Texniki olmayan maraqlı tərəflərə məlumat təqdimatı",
      ],
      outcome:
        "Məlumata əsaslanan qərarlara güvənən şirkətlərdə Məlumat Analitiki, Business Intelligence və ya Hesabat Analitiki rollarına keçin.",
    },
  },
  {
    title: "DevOps və CI/CD",
    icon: GitBranch,
    desc: "Docker, Kubernetes, Jenkins — avtomatlaşdırın və daha sürətli çatdırın.",
    bg: "bg-orange-600",
    duration: "5 ay",
    level: "Orta → İrəli",
    cert: "CKA / Docker DCA",
    detail: {
      overview:
        "DevOps inkişaf və əməliyyatlar arasındakı uçurumu bağlayır. Kod inteqrasiyasından deployment boru kəmərlərinə qədər hər şeyi Netflix, Amazon və Google-un istifadə etdiyi alətlərlə avtomatlaşdırmağı öyrənəcəksiniz.",
      topics: [
        "DevOps üçün Linux: shell skriptləşdirmə və avtomatlaşdırma",
        "Docker: konteyner, image və Docker Compose",
        "Kubernetes: pod, deployment, servis və Helm",
        "Jenkins və GitHub Actions CI/CD boru kəmərləri",
        "Konfiqurasiya idarəetməsi üçün Ansible",
        "Prometheus və Grafana ilə monitorinq",
      ],
      outcome:
        "Yüksək tələbatlı DevOps Mühəndisi, Site Reliability Engineer və ya Platform Mühəndisi rollarına yüksək maaşlarla uyğun olun.",
    },
  },
  {
    title: "Mobil İnkişaf",
    icon: Smartphone,
    desc: "React Native və Flutter ilə Android və iOS tətbiqləri.",
    bg: "bg-pink-700",
    duration: "5 ay",
    level: "Başlanğıc → Orta",
    cert: "CodeKloud Sertifikatı",
    detail: {
      overview:
        "Tək kod bazasından həm Android, həm iOS-da işləyən real mobil tətbiqlər qurun. Çarpaz platforma inkişaf framework-ləri, mobil UI dizayn prinsipləri və tətbiqlərinizi App Store və Google Play-ə necə nəşr edəcəyinizi öyrənəcəksiniz.",
      topics: [
        "JavaScript və Dart dil əsasları",
        "React Native: komponentlər, naviqasiya və state",
        "Flutter: widget-lər, mövzular və animasiyalar",
        "REST API-ləri və Firebase-ə tətbiqlərin qoşulması",
        "Yerli saxlama, push bildirişlər və icazələr",
        "App Store və Google Play nəşr prosesi",
      ],
      outcome:
        "Öz tətbiqinizi işə salın və ya minlərlə real istifadəçi tərəfindən istifadə edilən məhsullar quran Mobil Developer kimi çalışın.",
    },
  },
  {
    title: "Verilənlər Bazası Administrasiyası",
    icon: Database,
    desc: "MySQL, PostgreSQL, MongoDB — verilənlər bazalarını dizayn edin və idarə edin.",
    bg: "bg-teal-700",
    duration: "3 ay",
    level: "Başlanğıc → Orta",
    cert: "Oracle / MySQL Sert.",
    detail: {
      overview:
        "Hər tətbiqin arxasında etibarlı verilənlər bazası dayanır. Bu kurs həm relasional, həm NoSQL verilənlər bazalarını dizayn etməyi, optimallaşdırmağı və idarə etməyi öyrədir. Yedəkləmə, replikasiya və performans tənzimləmə kimi real ssenarilərlə məşğul olacaqsınız.",
      topics: [
        "Relasional verilənlər bazası dizaynı və normallaşdırma",
        "MySQL və PostgreSQL administrasiyası",
        "İrəli SQL: saxlanmış prosedurlar, görünüşlər, triggerlər",
        "MongoDB və NoSQL məlumat modelləşdirmə",
        "Verilənlər bazası yedəkləmə, bərpa və replikasiya",
        "Sorğu optimallaşdırması və performans tənzimləmə",
      ],
      outcome:
        "Kritik məlumat infrastrukturunu idarə edən Verilənlər Bazası Administratoru, Backend Developer və ya Məlumat Mühəndisi kimi çalışın.",
    },
  },
  {
    title: "Linux və Server Administrasiyası",
    icon: Terminal,
    desc: "Linux əsasları, shell skriptləşdirmə və server idarəetməsi.",
    bg: "bg-gray-800",
    duration: "3 ay",
    level: "Başlanğıc → Orta",
    cert: "CompTIA Linux+ / RHCSA",
    detail: {
      overview:
        "Linux dünya serverlərinin 90%-dən çoxuna güc verir. Ciddi IT karyerası üçün onu mənimsəmək vacibdir. Bu kurs sizi əsas komandalardan mürəkkəb shell skriptlər yazıb istehsal Linux serverlərini idarə etməyə qədər aparır.",
      topics: [
        "Linux fayl sistemi iyerarxiyası və naviqasiyası",
        "İstifadəçi və qrup idarəetməsi, icazələr və ACL-lər",
        "Proses idarəetməsi və sistem monitorinqi",
        "Avtomatlaşdırma üçün Bash shell skriptləşdirmə",
        "Paket idarəetməsi: apt, yum və dnf",
        "Veb, DNS və SSH server konfiqurasiyası",
      ],
      outcome:
        "Komandanızın güvendiyi Linux ekspertinə çevrilin və sysadmin, DevOps, bulud mühəndisliyi karyeralarının qapılarını açın.",
    },
  },
  {
    title: "Simsiz Şəbəkə və IoT",
    icon: Wifi,
    desc: "Wi-Fi texnologiyaları, IoT sistemləri və ağıllı cihaz idarəetməsi.",
    bg: "bg-cyan-700",
    duration: "3 ay",
    level: "Orta səviyyə",
    cert: "CWNA / IoT Sertifikatı",
    detail: {
      overview:
        "Dünya simsiz və bağlı olmağa doğru gedir. Bu kurs müəssisə Wi-Fi dizaynını, simsiz təhlükəsizliyi və sürətlə böyüyən Əşyaların İnterneti ekosistemini əhatə edir. Real cihazlar, sensorlar və simsiz kontrollerlərlə işləyəcəksiniz.",
      topics: [
        "802.11 Wi-Fi standartları və RF əsasları",
        "Simsiz şəbəkə dizaynı və sahə araşdırmaları",
        "WPA3, RADIUS və simsiz təhlükəsizlik ən yaxşı təcrübələri",
        "IoT arxitekturası: sensorlar, şlüzlər və platformalar",
        "IoT üçün MQTT protokolu və məlumat kommunikasiyası",
        "Ağıllı ev və sənaye IoT istifadə halları",
      ],
      outcome:
        "Sürətlə genişlənən bağlı cihazlar sənayesində Simsiz Şəbəkə Mühəndisi və ya IoT Həlləri Mütəxəssisi kimi çalışın.",
    },
  },
  {
    title: "Etik Hakerlik və Pentesting",
    icon: Lock,
    desc: "Penetrasiya testləri, zəiflik skanı və hücum simulyasiyası.",
    bg: "bg-rose-800",
    duration: "5 ay",
    level: "Orta → İrəli",
    cert: "CEH / eJPT / OSCP",
    detail: {
      overview:
        "Etik hakerlər qanuni olaraq sistemlər sınamaq üçün ödənilir. Bu irəli kurs sizi hücumçu kimi düşünməyi, sistemli şəkildə zəiflikləri tapmağı və təşkilatları qorumaq üçün hesabat hazırlamağı öyrədir.",
      topics: [
        "Penetrasiya testi metodologiyası (kəşfiyyat, skan, istismar)",
        "Kali Linux alətləri: Nmap, Metasploit, Burp Suite",
        "Veb tətbiq hücumları: SQLi, XSS, CSRF, IDOR",
        "Şifrə sındırma və imtiyaz artırma texnikaları",
        "Active Directory hücumları və lateral hərəkət",
        "Peşəkar penetrasiya testi hesabatlarının yazılması",
      ],
      outcome:
        "Sertifikatlı Penetrasiya Testçisi və ya Bug Bounty Hunter olun — texnologiya sahəsindəki ən maraqlı və yüksək maaşlı işlərdən biri.",
    },
  },
  {
    title: "Maşın Öyrənməsi və Süni İntellekt",
    icon: Bot,
    desc: "Python, TensorFlow və real ML model quruculuğu.",
    bg: "bg-purple-800",
    duration: "6 ay",
    level: "Orta → İrəli",
    cert: "TensorFlow Developer Sert.",
    detail: {
      overview:
        "Süni İntellekt hər sənəti yenidən formalaşdırır. Bu kurs real maşın öyrənməsi modellərini qurmağı, öyrətməyi və yerləşdirməyi öyrədir. Real biznes problemlərini simulyasiya edən verilənlər üzərində Python, TensorFlow və scikit-learn ilə işləyəcəksiniz.",
      topics: [
        "Məlumat elmi üçün Python: NumPy, pandas, Matplotlib",
        "Nəzarətli və nəzarətsiz öyrənmə alqoritmləri",
        "Neyron şəbəkələri və dərin öyrənmə əsasları",
        "TensorFlow və Keras model öyrədilməsi",
        "Təbii dil emalı və kompüter görüşü əsasları",
        "Flask və bulud API-ləri ilə model yerləşdirmə",
      ],
      outcome:
        "Texnologiyada ən yüksək maaşlı rollardan biri olan Maşın Öyrənməsi Mühəndisi, AI Developer və ya Məlumat Alimi kimi sahəyə qoşulun.",
    },
  },
  {
    title: "İT Layihə İdarəetməsi",
    icon: Clock,
    desc: "Agile, Scrum, PMP — texnoloji layihələri başdan sona idarə edin.",
    bg: "bg-amber-700",
    duration: "3 ay",
    level: "Başlanğıc → Orta",
    cert: "PMI / Scrum PSM I",
    detail: {
      overview:
        "Hər IT komandasının planlaşdıra, koordinasiya edə və vaxtında çatdıra bilən liderləri lazımdır. Bu kurs dünya üzrə ən yaxşı şirkətlər tərəfindən istifadə edilən Agile və ənənəvi layihə idarəetməsi metodologiyalarını real case study-lər və sprint simulyasiyaları ilə öyrədir.",
      topics: [
        "Layihə həyat dövrü: başlanğıc, planlaşdırma, icra, bağlanış",
        "Agile manifesti və Scrum framework dərin təhlili",
        "Sprint planlaması, backlog grooming və retrospektivlər",
        "Risk idarəetməsi və maraqlı tərəflərlə kommunikasiya",
        "Layihə izləmə üçün Jira və Trello",
        "PMP və PSM I imtahan hazırlığı",
      ],
      outcome:
        "IT Layihə Meneceri, Scrum Master və ya Məhsul Sahibi rollarına keçin və çarpaz funksional komandaları inamla rəhbərlik edin.",
    },
  },
  {
    title: "UI/UX Dizayn",
    icon: Award,
    desc: "Figma, istifadəçi tədqiqatı və məhsul dizayn prinsipləri.",
    bg: "bg-fuchsia-700",
    duration: "4 ay",
    level: "Başlanğıc → Orta",
    cert: "Google UX Design Sert.",
    detail: {
      overview:
        "Böyük məhsullar böyük dizayn tələb edir. Bu kurs istifadəçiləri araşdırmağı, interfeyslər dizayn etməyi və developerlərin qurabileceği interaktiv prototiplər yaratmağı öyrədir. Real dizayn portfeli qurmaq üçün sənaye standartı alət olan Figma-dan istifadə edəcəksiniz.",
      topics: [
        "İstifadəçi tədqiqatı: müsahibələr, anketlər və personalar",
        "İnformasiya arxitekturası və istifadəçi axışları",
        "Wireframing və lo-fi prototipləşdirmə",
        "Figma-da yüksək fidelity UI dizaynı",
        "İstifadəni test etmə və dizayn iterasiyası",
        "Dizayn sistemləri ilə developerlərə handoff",
      ],
      outcome:
        "3+ layihəli Figma portfeli qurun və UI/UX Dizayner, Məhsul Dizayneri və ya UX Tədqiqatçısı kimi rollara nail olun.",
    },
  },
  {
    title: "Blockchain və Web3",
    icon: GitBranch,
    desc: "Solidity, ağıllı müqavilələr və mərkəzləşdirilməmiş tətbiqlər.",
    bg: "bg-lime-800",
    duration: "4 ay",
    level: "Orta → İrəli",
    cert: "Sertifikatlı Blockchain Dev",
    detail: {
      overview:
        "Blockchain kriptovalyutanın ötəsindəki təchizat zəncirinə, səhiyyəyə, maliyyəyə hərəkət edir. Bu kurs Solidity-də ağıllı müqavilələr yazmağı, mərkəzləşdirilməmiş tətbiqlər qurmağı öyrədir.",
      topics: [
        "Blockchain əsasları: konsensus, qovşaqlar və zəncirlər",
        "Ethereum ekosistemi və EVM izahı",
        "Solidity proqramlaşdırması: müqavilələr, funksiyalar, hadisələr",
        "Hardhat və Truffle inkişaf mühitləri",
        "DApp frontend üçün Web3.js və ethers.js",
        "DeFi protokolları və NFT ağıllı müqavilə inkişafı",
      ],
      outcome:
        "Sürətlə böyüyən Web3 sənayesində Blockchain Developer və ya Ağıllı Müqavilə Mühəndisi kimi çalışın.",
    },
  },
  {
    title: "Rəqəmsal Marketinq və SEO",
    icon: BarChart2,
    desc: "Google Ads, SEO, sosial media və analitika.",
    bg: "bg-green-800",
    duration: "3 ay",
    level: "Başlanğıc → Orta",
    cert: "Google Rəqəmsal Marketinq",
    detail: {
      overview:
        "Hər biznesin rəqəmsal mövcudluğa ehtiyacı var. Bu praktiki kurs axtarış motoru optimallaşdırmasından ödənişli reklamlara və sosial media strategiyasına qədər tam rəqəmsal marketinq alət dəstini real kampaniyalar ilə əhatə edir.",
      topics: [
        "SEO əsasları: on-page, off-page və texniki",
        "Google Ads: axtarış, displey və alış-veriş kampaniyaları",
        "Sosial media marketinqi: Instagram, LinkedIn, Facebook",
        "E-poçt marketinqi və marketinq avtomatlaşdırması",
        "Google Analytics 4 və konversiya izləmə",
        "Veb üçün məzmun strategiyası və kopiraytinq",
      ],
      outcome:
        "Müstəqil kampaniyalar başladın və ya Rəqəmsal Marketinq Mütəxəssisi və ya SEO Analitiki kimi agentlik və daxili komandalara qoşulun.",
    },
  },
  {
    title: "Keyfiyyət Təminatı və Test",
    icon: CheckCircle,
    desc: "Selenium və Cypress ilə manual və avtomatik test.",
    bg: "bg-yellow-700",
    duration: "4 ay",
    level: "Başlanğıc → Orta",
    cert: "ISTQB Foundation",
    detail: {
      overview:
        "Hər böyük məhsulun istifadəçilər tapmadan əvvəl xətaları aşkarlayan QA mühəndisinə ehtiyacı var. Bu kurs həm manual, həm avtomatik test strategiyalarını, test case-lər yazmağı, avtomatlaşdırma framework-ləri qurmağı öyrədir.",
      topics: [
        "Proqram test əsasları və SDLC inteqrasiyası",
        "Test case dizaynı: ekvivalentlik bölmə, sərhəd dəyərləri",
        "Manual test: veb, mobil və API testi",
        "Java və ya Python ilə Selenium WebDriver",
        "Müasir JavaScript tətbiq testi üçün Cypress",
        "Postman və REST Assured ilə API testi",
      ],
      outcome:
        "Hər proqram şirkətindəki yüksək tələbatlı QA Mühəndisi və ya Test Avtomatlaşdırma Mühəndisi rollarına hər hansı proqram komandasına qoşulun.",
    },
  },
  {
    title: "ERP və Biznes Sistemləri",
    icon: BookOpen,
    desc: "SAP, Odoo və müəssisə resurs planlamasının əsasları.",
    bg: "bg-stone-700",
    duration: "3 ay",
    level: "Başlanğıc → Orta",
    cert: "SAP Sertifikatlı Müxbir",
    detail: {
      overview:
        "Müəssisə sistemləri hər böyük biznesin backend-ini idarə edir. Bu kurs ERP konsepsiyalarını tanıtır və SAP və Odoo ilə praktiki təcrübə verir — maliyyə, HR və əməliyyatları idarə etmək üçün minlərlə şirkət tərəfindən istifadə edilən platformalar.",
      topics: [
        "ERP konsepsiyaları: modullar, inteqrasiya və biznes prosesləri",
        "SAP S/4HANA naviqasiyası və əsas modullar (FI, MM, SD)",
        "Odoo community edition quraşdırılması və konfiqurasiyası",
        "Biznes prosesi xəritələşdirmə və tələb analizi",
        "Məlumat miqrasiyası və sistem inteqrasiyası əsasları",
        "ERP tətbiq layihəsinin həyat dövrü",
      ],
      outcome:
        "Əməliyyatlarını modernləşdirən müəssisələrdə ERP Məsləhətçisi, SAP Analitiki və ya Biznes Sistemləri Mütəxəssisi kimi çalışın.",
    },
  },
  {
    title: "İT Dəstəyi və Helpdesk",
    icon: Phone,
    desc: "Helpdesk bacarıqları, bilet sistemləri və müştəri dəstəyi.",
    bg: "bg-blue-700",
    duration: "2 ay",
    level: "Başlanğıc",
    cert: "CompTIA A+ / HDI",
    detail: {
      overview:
        "IT dəstəyi minlərlə uğurlu IT karyerası üçün giriş nöqtəsidir. Bu kurs sizi real istifadəçi problemlərini inamla həll etməyə, peşəkar bilet sistemlərindən istifadə etməyə hazırlayır.",
      topics: [
        "Hardware və proqram troubleshooting metodologiyası",
        "Windows 10/11 və macOS dəstəyi əsasları",
        "Active Directory istifadəçi idarəetməsi və parol sıfırlama",
        "Bilet sistemləri: ServiceNow və Jira Service Desk",
        "Uzaqdan dəstək alətləri: TeamViewer, AnyDesk, RDP",
        "Kommunikasiya bacarıqları və eskalasiya prosedurları",
      ],
      outcome:
        "Helpdesk Texniki və ya IT Dəstək Mütəxəssisi kimi ilk IT rolanızı əldə edin və hər IT ixtisasına doğru inkişaf edin.",
    },
  },
  ],
  ru: [
  {
    title: "IT и системная инженерия",
    icon: GraduationCap,
    desc: "ОС, железо и реальное системное администрирование с нуля.",
    bg: "bg-blue-900",
    duration: "4 месяца",
    level: "Начинающий → Средний",
    cert: "CompTIA A+",
    detail: {
      overview:
        "Этот курс переведёт вас от нуля до уверенного IT-специалиста. Вы узнаете, как компьютеры работают на уровне железа, освоите операционные системы Windows и Linux, и отработаете реальные задачи системного администрирования, используемые в корпоративных средах.",
      topics: [
        "Компоненты компьютерного железа и сборка",
        "Установка и настройка Windows и Linux",
        "Файловые системы, права доступа и управление пользователями",
        "Основы Active Directory и групповых политик",
        "Методология устранения неполадок и навыки helpdesk",
        "Виртуализация с VMware и VirtualBox",
      ],
      outcome:
        "Выпуститесь как junior IT-администратор, готовый к ролям helpdesk, sysadmin или IT-поддержки в реальных компаниях.",
    },
  },
  {
    title: "Сети и безопасность",
    icon: Network,
    desc: "Проектирование сетей, маршрутизация, коммутация и основы администрирования.",
    bg: "bg-indigo-700",
    duration: "5 месяцев",
    level: "Начинающий → Средний",
    cert: "Cisco CCNA",
    detail: {
      overview:
        "Погрузитесь в мир компьютерных сетей. От понимания того, как данные передаются через интернет, до настройки маршрутизаторов и коммутаторов в лабораторной среде — курс даёт практические навыки, востребованные работодателями.",
      topics: [
        "Основы моделей OSI и TCP/IP",
        "IP-адресация, разбивка на подсети и VLSM",
        "Настройка роутеров и коммутаторов Cisco (CLI)",
        "VLAN, STP, EtherChannel и транкинг",
        "Протоколы маршрутизации OSPF, EIGRP и BGP",
        "Инструменты мониторинга и устранения неполадок",
      ],
      outcome:
        "Подготовьтесь к сертификации Cisco CCNA и ролям сетевого администратора, NOC-инженера или сетевого аналитика.",
    },
  },
  {
    title: "Кибербезопасность",
    icon: Shield,
    desc: "Защитная безопасность, этичный хакинг и риск-мышление.",
    bg: "bg-red-700",
    duration: "5 месяцев",
    level: "Средний уровень",
    cert: "CompTIA Security+",
    detail: {
      overview:
        "Кибербезопасность — самый востребованный навык в IT сегодня. Курс учит, как думают злоумышленники и как реагируют защитники. Вы будете работать в реальных лабораторных средах, симулируя атаки и выстраивая защиту.",
      topics: [
        "Угрозы, уязвимости и векторы атак",
        "Межсетевые экраны, IDS/IPS и SIEM-системы",
        "Основы криптографии и PKI",
        "Политики безопасности, управление рисками и соответствие",
        "Основы реагирования на инциденты и цифровой криминалистики",
        "Практические лабы с Kali Linux и Wireshark",
      ],
      outcome:
        "Займите роли аналитика безопасности, SOC-аналитика или IT-специалиста по безопасности с прочной базой для продвинутых сертификаций.",
    },
  },
  {
    title: "Разработка программного обеспечения",
    icon: Code,
    desc: "Backend, frontend и полный стек веб-разработки.",
    bg: "bg-violet-700",
    duration: "6 месяцев",
    level: "Начинающий → Продвинутый",
    cert: "Сертификат CodeKloud",
    detail: {
      overview:
        "Хотите создавать сайты, API или полноценные приложения — этот курс охватывает всё. Вы будете писать реальный код с первого дня, создавать портфельные проекты и изучать современные рабочие процессы разработки.",
      topics: [
        "Основы HTML, CSS и JavaScript",
        "React.js для современной frontend-разработки",
        "Python или Node.js для backend-разработки",
        "REST API и интеграция с базами данных",
        "Git, GitHub и совместное кодирование",
        "Развёртывание приложений в облаке (Heroku, Vercel, AWS)",
      ],
      outcome:
        "Создайте профессиональное портфолио с 3–5 реальными проектами и претендуйте на роли junior-разработчика.",
    },
  },
  {
    title: "Облачные вычисления",
    icon: Cloud,
    desc: "AWS, Azure и GCP — облачная инфраструктура и развёртывание.",
    bg: "bg-sky-600",
    duration: "4 месяца",
    level: "Средний уровень",
    cert: "AWS Solutions Architect",
    detail: {
      overview:
        "Облако — уже не опция, это основа современных IT. Курс учит проектировать, развёртывать и управлять инфраструктурой на ведущих облачных платформах. Лабы проходят на реальных аккаунтах AWS, Azure и GCP.",
      topics: [
        "Облачные концепции: IaaS, PaaS, SaaS",
        "Основные сервисы AWS: EC2, S3, RDS, VPC, IAM",
        "Основы Azure и Azure AD",
        "Балансировка нагрузки, автомасштабирование и высокая доступность",
        "Лучшие практики облачной безопасности и оптимизация затрат",
        "Основы Infrastructure as Code с Terraform",
      ],
      outcome:
        "Получите сертификации AWS, Azure или GCP и работайте облачным инженером, архитектором или DevOps-инженером.",
    },
  },
  {
    title: "Аналитика данных",
    icon: BarChart2,
    desc: "SQL, Python, Power BI — превращайте сырые данные в решения.",
    bg: "bg-emerald-700",
    duration: "4 месяца",
    level: "Начинающий → Средний",
    cert: "Microsoft PL-300",
    detail: {
      overview:
        "Данные — это новая нефть, а аналитики — инженеры, которые её перерабатывают. Курс учит собирать, очищать, анализировать и визуализировать данные для принятия бизнес-решений. Предыдущий опыт не нужен.",
      topics: [
        "SQL от основ до сложных запросов и джойнов",
        "Python с pandas, NumPy и Matplotlib",
        "Техники очистки и трансформации данных",
        "Дизайн дашбордов Power BI и формулы DAX",
        "Статистическое мышление и анализ KPI",
        "Представление данных нетехническим стейкхолдерам",
      ],
      outcome:
        "Занимайте роли аналитика данных, специалиста по бизнес-аналитике в компаниях, принимающих решения на основе данных.",
    },
  },
  {
    title: "DevOps и CI/CD",
    icon: GitBranch,
    desc: "Docker, Kubernetes, Jenkins — автоматизируйте и поставляйте быстрее.",
    bg: "bg-orange-600",
    duration: "5 месяцев",
    level: "Средний → Продвинутый",
    cert: "CKA / Docker DCA",
    detail: {
      overview:
        "DevOps устраняет разрыв между разработкой и эксплуатацией. Вы научитесь автоматизировать всё — от интеграции кода до деплой-пайплайнов — с инструментами, которые используют Netflix, Amazon и Google.",
      topics: [
        "Linux для DevOps: написание скриптов и автоматизация",
        "Docker: контейнеры, образы и Docker Compose",
        "Kubernetes: поды, деплойменты, сервисы и Helm",
        "CI/CD-пайплайны Jenkins и GitHub Actions",
        "Ansible для управления конфигурацией",
        "Мониторинг с Prometheus и Grafana",
      ],
      outcome:
        "Получите высокооплачиваемые роли DevOps-инженера, Site Reliability Engineer или Platform Engineer.",
    },
  },
  {
    title: "Мобильная разработка",
    icon: Smartphone,
    desc: "Android и iOS-приложения с React Native и Flutter.",
    bg: "bg-pink-700",
    duration: "5 месяцев",
    level: "Начинающий → Средний",
    cert: "Сертификат CodeKloud",
    detail: {
      overview:
        "Создавайте реальные мобильные приложения для Android и iOS из единой кодовой базы. Вы изучите кроссплатформенные фреймворки, принципы мобильного UI-дизайна и научитесь публиковать приложения в App Store и Google Play.",
      topics: [
        "Основы JavaScript и Dart",
        "React Native: компоненты, навигация и состояние",
        "Flutter: виджеты, темы и анимации",
        "Подключение приложений к REST API и Firebase",
        "Локальное хранение, push-уведомления и разрешения",
        "Процесс публикации в App Store и Google Play",
      ],
      outcome:
        "Запустите собственное приложение или работайте мобильным разработчиком, создавая продукты для тысяч реальных пользователей.",
    },
  },
  {
    title: "Администрирование баз данных",
    icon: Database,
    desc: "MySQL, PostgreSQL, MongoDB — проектируйте и управляйте базами данных.",
    bg: "bg-teal-700",
    duration: "3 месяца",
    level: "Начинающий → Средний",
    cert: "Oracle / MySQL Cert",
    detail: {
      overview:
        "За каждым приложением стоит надёжная база данных. Курс учит проектировать, оптимизировать и администрировать как реляционные, так и NoSQL базы данных. Реальные сценарии: резервное копирование, репликация и настройка производительности.",
      topics: [
        "Проектирование реляционных баз данных и нормализация",
        "Администрирование MySQL и PostgreSQL",
        "Продвинутый SQL: хранимые процедуры, представления, триггеры",
        "MongoDB и моделирование данных NoSQL",
        "Резервное копирование, восстановление и репликация",
        "Оптимизация запросов и настройка производительности",
      ],
      outcome:
        "Работайте администратором баз данных, backend-разработчиком или инженером данных, управляя критической инфраструктурой.",
    },
  },
  {
    title: "Linux и администрирование серверов",
    icon: Terminal,
    desc: "Основы Linux, написание скриптов и управление серверами.",
    bg: "bg-gray-800",
    duration: "3 месяца",
    level: "Начинающий → Средний",
    cert: "CompTIA Linux+ / RHCSA",
    detail: {
      overview:
        "Linux управляет более 90% мировых серверов. Без него серьёзная IT-карьера невозможна. Курс ведёт вас от базовых команд до написания сложных shell-скриптов и управления production Linux-серверами.",
      topics: [
        "Иерархия файловой системы Linux и навигация",
        "Управление пользователями и группами, права и ACL",
        "Управление процессами и мониторинг системы",
        "Написание скриптов Bash для автоматизации",
        "Управление пакетами: apt, yum и dnf",
        "Настройка веб-, DNS- и SSH-серверов",
      ],
      outcome:
        "Станьте Linux-экспертом команды и откройте двери к карьере sysadmin, DevOps и облачного инженера.",
    },
  },
  {
    title: "Беспроводные сети и IoT",
    icon: Wifi,
    desc: "Wi-Fi технологии, IoT-системы и управление умными устройствами.",
    bg: "bg-cyan-700",
    duration: "3 месяца",
    level: "Средний уровень",
    cert: "CWNA / IoT Certificate",
    detail: {
      overview:
        "Мир становится беспроводным и взаимосвязанным. Курс охватывает корпоративный Wi-Fi-дизайн, беспроводную безопасность и быстро растущую экосистему Интернета вещей. Работа с реальными устройствами, датчиками и беспроводными контроллерами.",
      topics: [
        "Стандарты Wi-Fi 802.11 и основы радиочастот",
        "Проектирование беспроводных сетей и обследование объектов",
        "WPA3, RADIUS и лучшие практики беспроводной безопасности",
        "Архитектура IoT: датчики, шлюзы и платформы",
        "Протокол MQTT и передача данных для IoT",
        "Умный дом и промышленные IoT-сценарии",
      ],
      outcome:
        "Работайте инженером беспроводных сетей или специалистом по IoT-решениям в быстро расширяющейся индустрии подключённых устройств.",
    },
  },
  {
    title: "Этичный хакинг и пентестинг",
    icon: Lock,
    desc: "Тестирование на проникновение, сканирование уязвимостей и симуляция атак.",
    bg: "bg-rose-800",
    duration: "5 месяцев",
    level: "Средний → Продвинутый",
    cert: "CEH / eJPT / OSCP",
    detail: {
      overview:
        "Этичным хакерам платят за то, чтобы они взламывали системы — легально. Этот продвинутый курс учит думать как злоумышленник, системно находить уязвимости и составлять отчёты для защиты организаций.",
      topics: [
        "Методология пентеста (разведка, сканирование, эксплуатация)",
        "Инструменты Kali Linux: Nmap, Metasploit, Burp Suite",
        "Атаки на веб-приложения: SQLi, XSS, CSRF, IDOR",
        "Взлом паролей и техники повышения привилегий",
        "Атаки Active Directory и боковое перемещение",
        "Написание профессиональных отчётов по пентесту",
      ],
      outcome:
        "Станьте сертифицированным пентестером или Bug Bounty Hunter — одна из самых захватывающих и высокооплачиваемых профессий в IT.",
    },
  },
  {
    title: "Машинное обучение и ИИ",
    icon: Bot,
    desc: "Python, TensorFlow и построение реальных ML-моделей.",
    bg: "bg-purple-800",
    duration: "6 месяцев",
    level: "Средний → Продвинутый",
    cert: "TensorFlow Developer Cert",
    detail: {
      overview:
        "Искусственный интеллект меняет каждую отрасль. Курс учит строить, обучать и разворачивать реальные модели машинного обучения. Работа с Python, TensorFlow и scikit-learn на данных, симулирующих реальные бизнес-задачи.",
      topics: [
        "Python для data science: NumPy, pandas, Matplotlib",
        "Алгоритмы обучения с учителем и без учителя",
        "Нейронные сети и основы глубокого обучения",
        "Обучение моделей TensorFlow и Keras",
        "Основы обработки естественного языка и компьютерного зрения",
        "Развёртывание моделей с Flask и облачными API",
      ],
      outcome:
        "Войдите в сферу как инженер машинного обучения, AI-разработчик или дата-сайентист — одни из самых высокооплачиваемых ролей в IT.",
    },
  },
  {
    title: "Управление IT-проектами",
    icon: Clock,
    desc: "Agile, Scrum, PMP — управляйте технологическими проектами от начала до конца.",
    bg: "bg-amber-700",
    duration: "3 месяца",
    level: "Начинающий → Средний",
    cert: "PMI / Scrum PSM I",
    detail: {
      overview:
        "Каждой IT-команде нужны лидеры, умеющие планировать, координировать и сдавать проекты вовремя. Курс обучает методологиям Agile и традиционного управления проектами с реальными кейс-стади и спринт-симуляциями.",
      topics: [
        "Жизненный цикл проекта: инициация, планирование, исполнение, закрытие",
        "Agile-манифест и углублённый разбор Scrum",
        "Планирование спринтов, груминг бэклога и ретроспективы",
        "Управление рисками и коммуникация со стейкхолдерами",
        "Jira и Trello для отслеживания проектов",
        "Подготовка к экзаменам PMP и PSM I",
      ],
      outcome:
        "Займите роли IT-менеджера проектов, Scrum Master или Product Owner и уверенно руководите кросс-функциональными командами.",
    },
  },
  {
    title: "UI/UX дизайн",
    icon: Award,
    desc: "Figma, исследование пользователей и принципы продуктового дизайна.",
    bg: "bg-fuchsia-700",
    duration: "4 месяца",
    level: "Начинающий → Средний",
    cert: "Google UX Design Cert",
    detail: {
      overview:
        "Отличные продукты требуют отличного дизайна. Курс учит исследовать пользователей, проектировать интерфейсы и создавать интерактивные прототипы. Вы будете использовать Figma — отраслевой стандарт — для создания реального дизайн-портфолио.",
      topics: [
        "Исследование пользователей: интервью, опросы и персоны",
        "Информационная архитектура и пользовательские потоки",
        "Вайрфреминг и прототипирование низкой точности",
        "Высокоточный UI-дизайн в Figma",
        "Юзабилити-тестирование и итерация дизайна",
        "Передача проекта разработчикам с дизайн-системами",
      ],
      outcome:
        "Создайте Figma-портфолио из 3+ проектов и займите роли UI/UX-дизайнера, продуктового дизайнера или UX-исследователя.",
    },
  },
  {
    title: "Блокчейн и Web3",
    icon: GitBranch,
    desc: "Solidity, смарт-контракты и децентрализованные приложения.",
    bg: "bg-lime-800",
    duration: "4 месяца",
    level: "Средний → Продвинутый",
    cert: "Certified Blockchain Dev",
    detail: {
      overview:
        "Блокчейн выходит за рамки криптовалют в цепочки поставок, здравоохранение, финансы и другие сферы. Курс учит писать смарт-контракты на Solidity, создавать децентрализованные приложения и понимать архитектуру блокчейн-сетей.",
      topics: [
        "Основы блокчейна: консенсус, узлы и цепочки",
        "Экосистема Ethereum и EVM",
        "Программирование на Solidity: контракты, функции, события",
        "Среды разработки Hardhat и Truffle",
        "Web3.js и ethers.js для frontend DApp",
        "DeFi-протоколы и разработка NFT смарт-контрактов",
      ],
      outcome:
        "Работайте блокчейн-разработчиком или инженером смарт-контрактов в быстро растущей индустрии Web3.",
    },
  },
  {
    title: "Цифровой маркетинг и SEO",
    icon: BarChart2,
    desc: "Google Ads, SEO, социальные сети и аналитика.",
    bg: "bg-green-800",
    duration: "3 месяца",
    level: "Начинающий → Средний",
    cert: "Google Digital Marketing",
    detail: {
      overview:
        "Каждому бизнесу нужно цифровое присутствие. Практический курс охватывает весь инструментарий цифрового маркетинга — от SEO до платной рекламы и стратегии в социальных сетях — с использованием реальных кампаний.",
      topics: [
        "Основы SEO: on-page, off-page и технический SEO",
        "Google Ads: поисковые, медийные и торговые кампании",
        "Маркетинг в социальных сетях: Instagram, LinkedIn, Facebook",
        "Email-маркетинг и автоматизация маркетинга",
        "Google Analytics 4 и отслеживание конверсий",
        "Контент-стратегия и копирайтинг для веба",
      ],
      outcome:
        "Запускайте кампании самостоятельно или присоединяйтесь к агентствам как специалист по цифровому маркетингу или SEO-аналитик.",
    },
  },
  {
    title: "Обеспечение качества и тестирование",
    icon: CheckCircle,
    desc: "Ручное и автоматизированное тестирование с Selenium и Cypress.",
    bg: "bg-yellow-700",
    duration: "4 месяца",
    level: "Начинающий → Средний",
    cert: "ISTQB Foundation",
    detail: {
      overview:
        "Каждому отличному продукту нужен QA-инженер, который поймает баги до пользователей. Курс охватывает стратегии ручного и автоматизированного тестирования, написание тест-кейсов и интеграцию тестирования в CI/CD.",
      topics: [
        "Основы тестирования ПО и интеграция с SDLC",
        "Проектирование тест-кейсов: классы эквивалентности, граничные значения",
        "Ручное тестирование: веб, мобильное и API",
        "Selenium WebDriver с Java или Python",
        "Cypress для тестирования современных JavaScript-приложений",
        "API-тестирование с Postman и REST Assured",
      ],
      outcome:
        "Присоединяйтесь к любой команде разработки как QA-инженер или инженер по автоматизации тестирования — роли востребованы в каждой компании.",
    },
  },
  {
    title: "ERP и бизнес-системы",
    icon: BookOpen,
    desc: "SAP, Odoo и основы планирования корпоративных ресурсов.",
    bg: "bg-stone-700",
    duration: "3 месяца",
    level: "Начинающий → Средний",
    cert: "SAP Certified Associate",
    detail: {
      overview:
        "Корпоративные системы управляют бэкендом каждого крупного бизнеса. Курс знакомит с концепциями ERP и даёт практический опыт работы с SAP и Odoo — платформами, которые используют тысячи компаний для управления финансами, HR и операциями.",
      topics: [
        "Концепции ERP: модули, интеграция и бизнес-процессы",
        "Навигация SAP S/4HANA и основные модули (FI, MM, SD)",
        "Настройка Odoo community edition",
        "Картирование бизнес-процессов и анализ требований",
        "Основы миграции данных и интеграции систем",
        "Жизненный цикл проекта внедрения ERP",
      ],
      outcome:
        "Работайте ERP-консультантом, SAP-аналитиком или специалистом по бизнес-системам в компаниях, модернизирующих свои операции.",
    },
  },
  {
    title: "IT-поддержка и helpdesk",
    icon: Phone,
    desc: "Навыки helpdesk, системы тикетов и поддержка клиентов.",
    bg: "bg-blue-700",
    duration: "2 месяца",
    level: "Начинающий",
    cert: "CompTIA A+ / HDI",
    detail: {
      overview:
        "IT-поддержка — это точка входа для тысяч успешных IT-карьер. Курс готовит вас уверенно решать реальные проблемы пользователей, использовать профессиональные системы тикетов и обеспечивать отличное техническое обслуживание клиентов.",
      topics: [
        "Методология устранения неполадок железа и ПО",
        "Основы поддержки Windows 10/11 и macOS",
        "Управление пользователями Active Directory и сброс паролей",
        "Системы тикетов: ServiceNow и Jira Service Desk",
        "Инструменты удалённой поддержки: TeamViewer, AnyDesk, RDP",
        "Коммуникативные навыки и процедуры эскалации",
      ],
      outcome:
        "Получите первую IT-роль как helpdesk-техник или специалист IT-поддержки и двигайтесь дальше в любую IT-специализацию.",
    },
  },
  ],
};

export { COURSE_CARDS_I18N };
